import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/AuthContext";
import { API_BASE_URL } from "../../utils/config";
import axios from "axios";

import { Container, Stack, Typography, Divider } from "@mui/material";

import {
  LoginBackground,
  BackgroundOverlay,
  LoginContainer,
  LeftContainer,
  RightContainer,
  Logo,
  FormHeader,
  StyledTextField,
  PrimaryButton,
  SecondaryButton,
  StyledForgotPassword,
  BrandHint,
} from "./LoginStyle";

import logo from "../../assets/img/logo_oddis.png";
import backgroundImage from "../../assets/login/background-login.jpeg";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      // Se conserva la query (p. ej. /mapeo?analisisJob=… del correo del análisis).
      const from = location.state?.from;
      const redirectTo = from?.pathname ? `${from.pathname}${from.search || ""}` : "/";
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}auth/login`, {
        email,
        password,
      });

      if (response?.data) {
        login(response.data);
      } else {
        toast.error("Error al iniciar sesión. Intenta nuevamente.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error procesando la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = () => navigate("/register");

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    navigate("/passwordRecovery");
  };

  return (
      <LoginBackground bg={backgroundImage}>
        <BackgroundOverlay />

        <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 2, sm: 3 },
            }}
        >
          <LoginContainer>
            <LeftContainer>
              <Logo src={logo} alt="Company Logo" />
              <BrandHint>
                <span>Seguro</span>
                <span>•</span>
                <span>Simple</span>
                <span>•</span>
                <span>Rápido</span>
              </BrandHint>
            </LeftContainer>

            <RightContainer>
              <Stack spacing={2.0} sx={{ width: "100%", maxWidth: 380, mx: "auto" }}>
                <FormHeader>
                  <Typography
                      variant="h5"
                      sx={{ fontWeight: 900, letterSpacing: -0.4, lineHeight: 1.15 }}
                  >
                    Iniciar sesión
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.6 }}>
                    Ingresa tu correo y contraseña para continuar.
                  </Typography>
                </FormHeader>

                <Divider sx={{ opacity: 0.28 }} />

                <form onSubmit={handleSubmit} noValidate style={{ width: "100%" }}>
                  <Stack spacing={1.5}>
                    <StyledTextField
                        variant="outlined"
                        fullWidth
                        label="Correo"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        autoFocus
                        required
                        InputLabelProps={{ shrink: true }}
                    />

                    <StyledTextField
                        variant="outlined"
                        fullWidth
                        label="Contraseña"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                        InputLabelProps={{ shrink: true }}
                    />

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.1}
                        sx={{ pt: 0.2 }}
                    >
                      <SecondaryButton
                          fullWidth
                          variant="outlined"
                          type="button"
                          onClick={handleRegisterClick}
                          disabled={loading}
                      >
                        Crear cuenta
                      </SecondaryButton>

                      <PrimaryButton
                          fullWidth
                          variant="contained"
                          type="submit"
                          disabled={loading}
                      >
                        {loading ? "Ingresando..." : "Ingresar"}
                      </PrimaryButton>
                    </Stack>

                    <StyledForgotPassword
                        component="button"
                        type="button"
                        onClick={handleForgotPasswordClick}
                    >
                      ¿Olvidaste tu contraseña?
                    </StyledForgotPassword>
                  </Stack>
                </form>
              </Stack>
            </RightContainer>
          </LoginContainer>
        </Container>
      </LoginBackground>
  );
};

export default Login;
